import React from 'react';
import Joi from 'joi';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useFieldset, useDispatchCallback, useAsyncSubmit } from 'controls/hooks';
import { validateFreeTrialEmail } from 'actions/freeTrial';
import { Input } from 'components/atoms/Input/Input';
import { Button, SocialButton } from 'components/atoms/Button';
import { Header } from 'components/atoms/Header/Header';
import { signUp, googleLogin, facebookLogin } from 'actions/auth';
import { Language } from 'language/Language';
import 'styles/_form.scss';
import { strings } from './strings';
import './_SignUp.scss';

const signUpFrom = {
  name: '',
  email: '',
  password: '',
  password2: '',
  newsletter: false,
  terms: false,
};

const signUpSchema = {
  name: Joi.string().required().label('name'),
  email: Joi.string().max(50).required().label('email'),
  password: Joi.string().required().label('password').ruleset.pattern(/^(?=.*[a-z])(?=.*[0-9])(?=.{6,})/).max(30).min(6).message('Password needs to have a minimum length of 6, at least one letter and one number.'),
  password2: Joi.string().required().label('password').valid(Joi.ref('password')).max(30).min(6).messages({ 'any.only': 'Confirmation password must be the same as the password' }),
  newsletter: Joi.boolean().label('newsletter'),
  terms: Joi.boolean().required().label('terms').invalid(false),
};

const SignUp = () => {
  const { countryUser } = useSelector((state) => state.siteInfo);
  const language = Language();
  const dispatch = useDispatch();
  const history = useHistory();
  const { location } = history;

  const handleCreateAUser = async () => {
    try {
      // This is for the api to validate if the user has a free trial and create the corresponding entitlement
      await dispatch(validateFreeTrialEmail());
    } catch (error) {
      // we don't need to handle exceptions or stop the flow
    }
    const isMyAccount = location.pathname.split('/')[2] === '私のアカウント' || location.pathname.split('/')[1] === 'my-account';
    if (isMyAccount) {
      const isJapanLanguage = language === 'japan';
      const isFromJapan = countryUser === 'JP';
      const url = isFromJapan ? `${isJapanLanguage ? 'ja/' : ''}voucher` : isJapanLanguage ? 'ja/' : '';
      if (isFromJapan) {
        history.push(`/${url}`);
      } else {
        /* the reason for using window.location.replace instead of history.push is because as the project is in wordpress
        we don't control all the routes so it wouldn't work to use a history.push to home it would take us to an empty page
        since this url exists inside wordpress but it doesn't inside this app */
        window.location.replace(`${window.location.origin}/${url}`);
      }
    }
  };
  const {
    fields: {
      name,
      email,
      password,
      password2,
      newsletter,
      terms,
    }, submit, loading,
  } = useFieldset({
    schema: signUpSchema,
    source: signUpFrom,
    onSubmit: useAsyncSubmit(useDispatchCallback(signUp), handleCreateAUser),
  });

  const google = () => {
    dispatch(googleLogin());
  };

  const facebook = () => {
    dispatch(facebookLogin());
  };

  return (
    <>
      <Header text={strings[language].title} />
      <form className='form' onSubmit={submit}>
        <Input
          classes='input'
          name='name'
          field={name}
          placeholder={strings[language].name}
        />
        <Input
          classes='input'
          name='email'
          field={email}
          type='email'
          placeholder={strings[language].email}
        />
        <Input
          classes='input'
          name='password'
          field={password}
          isPassword
          type='password'
          placeholder={strings[language].password}
        />
        <Input
          classes='input'
          name='password2'
          field={password2}
          isPassword
          type='password'
          style={{ marginTop: '0' }}
          placeholder={strings[language].confirmPassword}
        />
        <div className='checkbox-container'>
          <Input classes='checkbox' type='checkbox' name='newsletter' field={newsletter} />
          <label>{strings[language].newsletter}</label>
        </div>
        <div className='checkbox-container'>
          <Input classes={terms.errored ? 'input-err checkbox' : 'checkbox'} type='checkbox' name='terms' field={terms} />
          <label>
            {language === 'english' && 'I accept the'}
            {' '}
            <a
              href={strings[language].policyLink}
              target='_blank'
              rel='noreferrer'
              alt='Privacity Policy'
            >
              {strings[language].policy}
            </a>
            {' '}
            ,
            {' '}
            <a href={strings[language].termsLink} target='_blank' rel='noreferrer' alt='Terms of Service'>{strings[language].terms}</a>
            *
          </label>
        </div>
        <Button
          className={!terms.value ? 'button-errored sign-up__btn' : 'sign-up__btn'}
          loading={loading}
          text={strings[language].signUp}
          type='submit'
        />
      </form>
      <div>
        <p className='sign-up__callToAction'>{strings[language].or}</p>
        <div className='sign-up-buttons'>
          <SocialButton
            text={strings[language].facebook}
            icon='facebook f'
            onClick={facebook}
          />
          <SocialButton
            text={strings[language].google}
            icon='google'
            isImage
            onClick={google}
          />
        </div>
      </div>
    </>
  );
};

export default SignUp;
